import React from 'react'
import { Box, Typography } from '@mui/material'
import { WherebyCallOwnershipStatus } from '../../../../../../pure-js/enums/Whereby'
import {
  shouldAskBeforeConnectingWhereby,
  type WherebyStatusRequiringAsk
} from '../../../../../../pure-js/libs/wherebyCallOwnership'
import { Button } from '../../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { TESTIDS } from '../../../../config/testids'

interface WherebyCallBlockedPanelProps {
  status: WherebyCallOwnershipStatus
  /** Ends the other connection and continues the call here — the user's explicit confirmation. */
  onConfirmTakeover: () => void
  onRetry: () => void
  /** Optional escape hatch (e.g. drop this tab's pending call). */
  onCancel?: () => void
}

// One entry per state that requires asking — the Record type makes a missing message a compile error.
const PANEL_COPY_KEYS: Record<WherebyStatusRequiringAsk, { title: string; description: string }> = {
  [WherebyCallOwnershipStatus.BLOCKED]: {
    title: 'whereby.ownership.blockedTitle',
    description: 'whereby.ownership.blockedDescription'
  },
  [WherebyCallOwnershipStatus.BLOCKED_IN_TAB]: {
    title: 'whereby.ownership.inThisTabTitle',
    description: 'whereby.ownership.inThisTabDescription'
  },
  [WherebyCallOwnershipStatus.ERROR]: {
    title: 'whereby.ownership.errorTitle',
    description: 'whereby.ownership.errorDescription'
  }
}

/**
 * Shown INSTEAD of the call whenever this surface may not connect: the user's single allowed Whereby
 * connection is live somewhere else, or on another surface in this tab, or we could not verify it.
 *
 * The message stays deliberately generic (no device, browser or timing details) — the user only needs to
 * know that one call runs at a time and that continuing here closes the other one.
 *
 * The confirm button is the only thing in the app that ends a call somewhere else, so it always sits
 * behind this explicit confirmation.
 */
export const WherebyCallBlockedPanel: React.FC<WherebyCallBlockedPanelProps> = ({
  status,
  onConfirmTakeover,
  onRetry,
  onCancel
}) => {
  const { t } = useTranslation()

  // Any other status means the call is fine (or still being checked) — this panel has nothing to say.
  if (!shouldAskBeforeConnectingWhereby(status)) return null
  const copyKeys = PANEL_COPY_KEYS[status]

  const canTakeOverTheCall = status === WherebyCallOwnershipStatus.BLOCKED
  const canRetryTheCheck = status === WherebyCallOwnershipStatus.ERROR

  return (
    <Box
      data-testid={TESTIDS.WHEREBY_CALL_BLOCKED_PANEL}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 1.5, sm: 1.5, md: 2 },
        minHeight: '200px',
        p: { xs: 2, sm: 2.5, md: 3 },
        borderRadius: '12px',
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        border: `1px solid ${INTERAX_COLORS.white10}`,
        textAlign: 'center'
      }}
    >
      <Typography
        sx={{ fontSize: { xs: '15px', sm: '16px', md: '16px' }, fontWeight: 600, color: INTERAX_COLORS.white }}
      >
        {t(copyKeys.title)}
      </Typography>
      <Typography sx={{ fontSize: { xs: '13px', sm: '13px', md: '14px' }, color: INTERAX_COLORS.white70 }}>
        {t(copyKeys.description)}
      </Typography>

      {canTakeOverTheCall ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5 }}>
          <Button variant="primary" onClick={onConfirmTakeover} data-testid={TESTIDS.WHEREBY_CALL_TAKEOVER_BTN}>
            {t('whereby.ownership.moveHere')}
          </Button>
          {onCancel ? (
            <Button variant="secondary" onClick={onCancel} data-testid={TESTIDS.WHEREBY_CALL_TAKEOVER_CANCEL_BTN}>
              {t('whereby.ownership.notNow')}
            </Button>
          ) : null}
        </Box>
      ) : null}

      {canRetryTheCheck ? (
        <Button variant="primary" onClick={onRetry}>
          {t('whereby.global.retry')}
        </Button>
      ) : null}
    </Box>
  )
}
